import { forwardRef } from 'react';
import FormInput from '../FormInput';
import { useLanguage } from '@/chassis/ui/i18n/useLanguage';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';

type I18nLabel = Record<Language, string>;

// ============================================
// KeyValueObject Types - Internal Types do Backend
// ============================================

// KeyValueObject (simple key-value pair)
interface KeyValueObjectProps {
    value?: { key: string; value: string };
    onChange?: (value: { key: string; value: string }) => void;
    disabled?: boolean;
    className?: string;
    keyPlaceholder?: string;
    valuePlaceholder?: string;
    i18nKeyPlaceholder?: I18nLabel;
    i18nValuePlaceholder?: I18nLabel;
}

export const KeyValueObject = forwardRef<HTMLDivElement, KeyValueObjectProps>(({ value = { key: '', value: '' }, onChange, disabled, className = '', keyPlaceholder = 'Chave', valuePlaceholder = 'Valor', i18nKeyPlaceholder, i18nValuePlaceholder }, ref) => {
    const lang = useLanguage();
    const displayKeyPlaceholder = i18nKeyPlaceholder ? i18nKeyPlaceholder[lang] : keyPlaceholder;
    const displayValuePlaceholder = i18nValuePlaceholder ? i18nValuePlaceholder[lang] : valuePlaceholder;
    const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.({ key: e.target.value, value: value.value });
    };

    const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.({ key: value.key, value: e.target.value });
    };

    return (
        <div ref={ref} className={`flex gap-2 ${className}`}>
            <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Chave</label>
                <FormInput type="text" value={value.key} onChange={handleKeyChange} disabled={disabled} placeholder={displayKeyPlaceholder} />
            </div>
            <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
                <FormInput type="text" value={value.value} onChange={handleValueChange} disabled={disabled} placeholder={displayValuePlaceholder} />
            </div>
        </div>
    );
});
KeyValueObject.displayName = 'KeyValueObject';

// KeyValueObjectList (multiple key-value pairs)
interface KeyValueObjectListProps {
    value?: Array<{ key: string; value: string }>;
    onChange?: (value: Array<{ key: string; value: string }>) => void;
    disabled?: boolean;
    className?: string;
    maxItems?: number;
}

export const KeyValueObjectList = forwardRef<HTMLDivElement, KeyValueObjectListProps>(({ value = [], onChange, disabled, className = '', maxItems }, ref) => {
    const handleAdd = () => {
        if (maxItems && value.length >= maxItems) return;
        onChange?.([...value, { key: '', value: '' }]);
    };

    const handleRemove = (index: number) => {
        onChange?.(value.filter((_, i) => i !== index));
    };

    const handleUpdate = (index: number, updated: { key: string; value: string }) => {
        const newValue = [...value];
        newValue[index] = updated;
        onChange?.(newValue);
    };

    return (
        <div ref={ref} className={className}>
            <div className="space-y-3">
                {value.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start p-3 border border-gray-300 rounded-md bg-gray-50">
                        <KeyValueObject value={item} onChange={(updated) => handleUpdate(index, updated)} disabled={disabled} className="flex-1" />
                        {!disabled && (
                            <button type="button" onClick={() => handleRemove(index)} className="mt-7 px-3 py-2 text-red-600 hover:text-red-800 border border-red-300 rounded-md">
                                ×
                            </button>
                        )}
                    </div>
                ))}
            </div>
            {!disabled && (!maxItems || value.length < maxItems) && (
                <button type="button" onClick={handleAdd} className="mt-3 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 border border-blue-300 rounded-md hover:bg-blue-50">
                    + Adicionar par chave-valor
                </button>
            )}
            {maxItems && (
                <p className="mt-2 text-xs text-gray-500">
                    {value.length}/{maxItems} pares
                </p>
            )}
        </div>
    );
});
KeyValueObjectList.displayName = 'KeyValueObjectList';
